import { type QueryClient, mutationOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { notificationSettingQueryKeys } from "./query-keys";
import { notificationsUrl } from "../constants";
import { setSettingsEnabled } from "../setting-utils";
import type {
  GetNotificationSettingsResponse,
  NotificationSetting,
  NotificationSettingEntry,
  NotificationSettingText,
} from "../types";

import { instance } from "@/lib/axios/instance";
import useToast from "@/lib/hooks/useToast";

type SettingTarget = Pick<NotificationSettingEntry, "id" | "types">;

type SaveFeedback = {
  onSaved: (enabled: NotificationSetting["enabled"]) => void;
  onSaveError: () => void;
};

export function notificationSettingMutationOptions(
  queryClient: QueryClient,
  { id, types }: SettingTarget,
  feedback: SaveFeedback,
) {
  return mutationOptions({
    mutationKey: notificationSettingQueryKeys.updates(),
    scope: { id: notificationSettingQueryKeys.update(id).join(":") },
    mutationFn: async (enabled: NotificationSetting["enabled"]) => {
      await Promise.all(types.map((type) => instance.post(notificationsUrl("Settings"), { type, enabled })));
    },
    onMutate: async (enabled) => {
      await queryClient.cancelQueries({ queryKey: notificationSettingQueryKeys.list() });
      queryClient.setQueryData<GetNotificationSettingsResponse>(
        notificationSettingQueryKeys.list(),
        (prev) => setSettingsEnabled(prev, { types, enabled }),
      );
    },
    onSuccess: (_, enabled) => feedback.onSaved(enabled),
    onError: () => feedback.onSaveError(),
    onSettled: async () => {
      if (queryClient.isMutating({ mutationKey: notificationSettingQueryKeys.updates() }) !== 1) return;
      await queryClient.invalidateQueries({ queryKey: notificationSettingQueryKeys.list() });
    },
  });
}

function useSaveFeedback(entryId: NotificationSettingEntry["id"], label: NotificationSettingText["label"]): SaveFeedback {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const id = `notification-setting-${entryId}`;

  return {
    onSaved: (enabled) =>
      showToast(t(enabled ? "notificationSettings.savedOn" : "notificationSettings.savedOff", { label }), "success", { id }),
    onSaveError: () => showToast(t("notificationSettings.saveError"), "error", { id }),
  };
}

export function useUpdateNotificationSettingMutation(
  target: SettingTarget,
  label: NotificationSettingText["label"],
) {
  const queryClient = useQueryClient();
  const feedback = useSaveFeedback(target.id, label);

  return useMutation(notificationSettingMutationOptions(queryClient, target, feedback));
}
